import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { EmptyState } from '@/components/EmptyState/EmptyState'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useGetImpersonationReportQuery } from '@/store/api/Impersonation/Impersonation.query'

import { Chip } from '../../components/Chip/Chip'

import { ImpersonationProfileItem } from './components/ImpersonationProfileItem'

interface LocationState {
  id?: string
}

export const ImpersonationReport = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LocationState
  const reportId = state?.id

  const { data, isLoading, isError } = useGetImpersonationReportQuery(
    { id: reportId as string },
    { skip: !reportId },
  )

  if (!reportId) {
    navigate(AppRoutes.reports, { replace: true })
    return null
  }

  const report = data?.info
  const verifiedProfiles = report?.verifiedProfiles || []
  const suspectedProfiles = report?.suspectedProfiles || []

  return (
    <WeScreen
      useNextButton={false}
      isLoading={isLoading}
      isError={isError}
      useHeaderBackButton
      onClickBack={() => navigate(AppRoutes.reports)}
    >
      <div className="flex w-full h-full justify-center overflow-y-auto custom-scrollbar">
        <div className="flex flex-col gap-8 w-2/3 max-md:w-full max-lg:w-3/4 py-4">
          <div className="flex flex-col gap-2">
            <p className="text-3xl max-md:text-2xl font-medium text-neutralColor">
              {t('impersonation.report.title')}
            </p>
            <p className="text-base font-normal text-neutralColor80">
              {report?.name}
            </p>
          </div>

          {/* Suspected impersonators — profiles found OUTSIDE the user's networks */}
          <div className="flex flex-col gap-4 bg-baseColorHighlight rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <p className="text-xl font-medium text-neutralColor">
                {t('impersonation.report.suspectedTitle')}
              </p>
              <Chip
                variant={suspectedProfiles.length === 0 ? 'green' : 'red'}
                size="tag"
                text={`${suspectedProfiles.length}`}
              />
            </div>
            <p className="text-sm font-normal text-neutralColor80">
              {t('impersonation.report.suspectedSubtitle')}
            </p>

            {suspectedProfiles.length === 0 ? (
              <EmptyState text={t('impersonation.report.emptySuspected')} />
            ) : (
              <div className="flex flex-col gap-3">
                {suspectedProfiles.map((profile) => (
                  <ImpersonationProfileItem
                    key={profile.normalizedUrl || profile.url}
                    userName={profile.userName}
                    platform={profile.platform}
                    url={profile.url}
                    image={profile.image}
                    isSuspected
                  />
                ))}
              </div>
            )}
          </div>

          {/* The user's own verified networks */}
          <div className="flex flex-col gap-4 bg-baseColorHighlight rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <p className="text-xl font-medium text-neutralColor">
                {t('impersonation.report.verifiedTitle')}
              </p>
              <Chip
                variant="green"
                size="tag"
                text={`${verifiedProfiles.length}`}
              />
            </div>
            <p className="text-sm font-normal text-neutralColor80">
              {t('impersonation.report.verifiedSubtitle')}
            </p>

            <div className="flex flex-col gap-3">
              {verifiedProfiles.map((profile) => (
                <ImpersonationProfileItem
                  key={profile.normalizedUrl || profile.url}
                  userName={profile.userName}
                  platform={profile.platform}
                  url={profile.url}
                  image={profile.image}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </WeScreen>
  )
}
